/**
 * Utilitários de formatação de moeda com suporte a internacionalização
 * Centraliza toda a lógica de formatação monetária do sistema
 * 
 * Idiomas suportados: pt-BR, en-US, es-ES
 * Moedas: BRL, USD, EUR
 * 
 * Versão: 1.0.0
 */

/**
 * Mapeamento de idioma → locale e moeda padrão
 */
const LOCALE_CURRENCY_MAP: Record<string, { locale: string; currency: string; decimalSeparator: string; thousandsSeparator: string }> = {
  'pt-BR': { locale: 'pt-BR', currency: 'BRL', decimalSeparator: ',', thousandsSeparator: '.' },
  'en-US': { locale: 'en-US', currency: 'USD', decimalSeparator: '.', thousandsSeparator: ',' },
  'es-ES': { locale: 'es-ES', currency: 'EUR', decimalSeparator: ',', thousandsSeparator: '.' },
};

/**
 * Símbolos das moedas suportadas
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  'BRL': 'R$',
  'USD': '$',
  'EUR': '€',
};

/**
 * Obtém configuração de moeda baseada no idioma
 * @param language - Código do idioma (ex: 'pt-BR', 'en-US')
 * @returns Configuração de locale, moeda e separadores
 */
export function getCurrencyConfig(language?: string): { 
  locale: string; 
  currency: string; 
  decimalSeparator: string; 
  thousandsSeparator: string;
} {
  const lang = language || 'pt-BR';
  return LOCALE_CURRENCY_MAP[lang] || LOCALE_CURRENCY_MAP['pt-BR'];
}

/**
 * Formata valor numérico como moeda
 * @param value - Valor numérico em reais (ex: 1234.56)
 * @param options - Opções de formatação
 * @returns String formatada (ex: "R$ 1.234,56")
 */
export function formatCurrency(
  value: number,
  options?: { currency?: string; language?: string }
): string {
  const config = getCurrencyConfig(options?.language);
  const currency = options?.currency || config.currency;
  
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Remove formatação de moeda e retorna valor numérico
 * @param value - String formatada (ex: "R$ 1.234,56")
 * @param language - Código do idioma para identificar separadores
 * @returns Valor numérico (ex: 1234.56)
 */
export function parseCurrencyValue(value: string, language?: string): number {
  if (!value) return 0;
  
  const config = getCurrencyConfig(language);
  
  // Remove tudo exceto dígitos e separadores
  let cleaned = value.replace(/[^\d.,]/g, '');
  
  if (!cleaned) return 0;
  
  // Converte baseado no separador decimal do idioma
  if (config.decimalSeparator === ',') {
    // pt-BR e es-ES: 1.234,56 → 1234.56
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // en-US: 1,234.56 → 1234.56
    cleaned = cleaned.replace(/,/g, '');
  }
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Aplica máscara de moeda progressiva enquanto usuário digita
 * @param value - String de entrada do usuário
 * @param options - Opções de formatação
 * @returns String com máscara de moeda aplicada
 */
export function applyCurrencyInputMask(
  value: string,
  options?: { currency?: string; language?: string }
): string {
  const config = getCurrencyConfig(options?.language);
  
  // Remove tudo que não é dígito
  const digits = value.replace(/\D/g, '');
  
  if (digits.length === 0) return '';
  
  // Converte para centavos e depois para valor decimal
  const numericValue = parseInt(digits, 10) / 100;
  
  // Formata com o locale correto
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: options?.currency || config.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
}

/**
 * Retorna placeholder apropriado para input de moeda
 * @param options - Opções de formatação
 * @returns Placeholder formatado (ex: "R$ 0,00")
 */
export function getCurrencyPlaceholder(
  options?: { currency?: string; language?: string }
): string {
  return formatCurrency(0, options);
}

/**
 * Retorna símbolo da moeda
 * @param currency - Código da moeda (ex: 'BRL', 'USD')
 * @returns Símbolo da moeda (ex: "R$", "$")
 */
export function getCurrencySymbol(currency?: string): string {
  return CURRENCY_SYMBOLS[currency || 'BRL'] || CURRENCY_SYMBOLS['BRL'];
}

/**
 * Converte valor de centavos para reais
 * @param cents - Valor em centavos
 * @returns Valor em reais
 */
export function centsToDecimal(cents: number): number {
  return cents / 100;
}

/**
 * Converte valor de reais para centavos
 * @param decimal - Valor em reais
 * @returns Valor em centavos
 */
export function decimalToCents(decimal: number): number {
  return Math.round(decimal * 100);
}
